/**
 * IntentSkeleton — the content-shaped placeholder committed the moment
 * `research.intent` lands, so the answer area already has the shape of the
 * answer that is coming (timeline rail, bullet rows, source cards, …) instead
 * of a generic block that reflows when the response arrives.
 */

import React from 'react';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { ResearchIntent } from './types';

/** Which Skeleton variant matches each answer shape. */
export function skeletonVariantFor(intent: ResearchIntent | null): 'detail' | 'list' | 'card' {
  switch (intent) {
    // A draft is a long document too — same detail shape as a memo.
    case 'memo':
    case 'deep_research':
    case 'draft':
      return 'detail';
    case 'chronology':
    case 'bullets':
      return 'list';
    case 'find_document':
      return 'card';
    case 'overview':
    case 'qa':
    default:
      return 'card';
  }
}

/** Rows worth showing per shape — a timeline reads longer than a Q&A. */
function rowsFor(intent: ResearchIntent | null): number {
  switch (intent) {
    case 'chronology':
      return 5;
    case 'bullets':
      return 4;
    case 'find_document':
      return 3;
    default:
      return 3;
  }
}

interface Props {
  intent: ResearchIntent | null;
}

const IntentSkeleton: React.FC<Props> = ({ intent }) => (
  <div className="mt-1" data-testid="intent-skeleton" data-intent={intent || 'unknown'}>
    <Skeleton variant={skeletonVariantFor(intent)} rows={rowsFor(intent)} />
  </div>
);

export default IntentSkeleton;
